import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import PERMISSIONS from 'constants/permissions';
import { useMutationQuestionImportCourse } from 'gql/mutations/question';
import { useQueryCoursesMe, useQueryCourseContent } from 'gql/queries/course';
import extract from 'helpers/extract';
import { trackEvent, reportError } from 'lib/tracking';

import QuestionImportCourse from './QuestionImportCourse';

const QuestionImportCourseContainer = ({ course, handleModalClose }) => {
  const { t } = useTranslation();
  const { isLoading: isLoadingCourses, data: dataCourses } =
    useQueryCoursesMe();
  const courses = isLoadingCourses
    ? []
    : dataCourses.coursesMe.filter((c) => c.level >= PERMISSIONS.COLLABORATOR);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const { isLoading: isLoadingCourse, data: dataCourse } =
    useQueryCourseContent({
      skip: !selectedCourseId,
      variables: {
        courseId: selectedCourseId,
      },
    });
  const selectedCourse = isLoadingCourse ? null : dataCourse.course;
  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        chapterId: yup
          .string()
          .trim()
          .matches(
            /^|([0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/i,
          ),
        selectedCourseId: yup
          .string()
          .trim()
          .required(
            t(
              'Course/Teacher/Editor/QuestionImport/QuestionImportCourse/course-error-required',
            ),
          )
          .matches(
            /^|([0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/i,
          ),
        questionIds: yup
          .array()
          .of(yup.string().required().trim().uuid())
          .compact()
          .test(
            'length',
            t(
              'Course/Teacher/Editor/QuestionImport/QuestionImportCourse/questions-error-required',
            ),
            (questions) => questions.length > 0,
          ),
      }),
    [t],
  );
  const mutationQuestionImportCourse = useMutationQuestionImportCourse();
  const handleQuestionsImport = useCallback(
    async (values, form) => {
      try {
        form.setStatus(null);
        const variables = extract(values, validationSchema);
        const {
          data: { questionImportCourse },
        } = await mutationQuestionImportCourse({
          ...variables,
          courseId: course.id,
        });
        handleModalClose(questionImportCourse);
        trackEvent('question', 'import-course');
      } catch (err) {
        form.setStatus('500');
        reportError('question', 'import-course', err);
      }
    },
    [course, handleModalClose, mutationQuestionImportCourse, validationSchema],
  );
  return (
    <QuestionImportCourse
      chapters={course.chapters}
      courses={courses}
      handleCourseIsSelected={setSelectedCourseId}
      selectedCourse={selectedCourse}
      handleQuestionsImport={handleQuestionsImport}
      validationSchema={validationSchema}
      t={t}
    />
  );
};

QuestionImportCourseContainer.propTypes = {
  course: PropTypes.object.isRequired,
  handleModalClose: PropTypes.func.isRequired,
};

export default QuestionImportCourseContainer;
